// React is imported for JSX
import { NavLink } from 'react-router-dom';
import { Home, Mic2, Settings, BarChart2, Volume2, Heart, History, Users, Receipt } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

const Sidebar = () => {
  const user = useSelector((state: RootState) => state.auth.user);

  const navItems = [
    { 
      path: '/dashboard', 
      icon: Home, 
      label: 'Dashboard',
      colors: {
        bg: 'bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20',
        icon: 'text-blue-600 dark:text-blue-400'
      }
    },
    { 
      path: '/purchases', 
      icon: Receipt, 
      label: 'Purchase History',
      colors: {
        bg: 'bg-gradient-to-br from-teal-100 to-cyan-100 dark:from-teal-900/20 dark:to-cyan-900/20',
        icon: 'text-teal-600 dark:text-teal-400'
      }
    },
    { 
      path: '/voices', 
      icon: Mic2, 
      label: 'Voice Library',
      colors: {
        bg: 'bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20',
        icon: 'text-purple-600 dark:text-purple-400'
      }
    },
    { 
      path: '/favorites', 
      icon: Heart, 
      label: 'Favorite Voices',
      colors: {
        bg: 'bg-gradient-to-br from-rose-100 to-red-100 dark:from-rose-900/20 dark:to-red-900/20',
        icon: 'text-rose-600 dark:text-rose-400'
      }
    },
    { 
      path: '/generations', 
      icon: History, 
      label: 'My Generations',
      colors: {
        bg: 'bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20',
        icon: 'text-amber-600 dark:text-amber-400'
      }
    },
    { 
      path: '/tts', 
      icon: Volume2, 
      label: 'Text to Speech',
      colors: {
        bg: 'bg-gradient-to-br from-emerald-100 to-green-100 dark:from-emerald-900/20 dark:to-green-900/20',
        icon: 'text-emerald-600 dark:text-emerald-400'
      }
    },
    { 
      path: '/settings', 
      icon: Settings, 
      label: 'Settings',
      colors: {
        bg: 'bg-gradient-to-br from-cyan-100 to-teal-100 dark:from-cyan-900/20 dark:to-teal-900/20',
        icon: 'text-cyan-600 dark:text-cyan-400'
      }
    },
  ];

  // Add admin-only menu items
  if (user?.role === 'admin') {
    navItems.push(
      { 
        path: '/admin/users', 
        icon: Users, 
        label: 'User Management',
        colors: {
          bg: 'bg-gradient-to-br from-violet-100 to-purple-100 dark:from-violet-900/20 dark:to-purple-900/20',
          icon: 'text-violet-600 dark:text-violet-400'
        }
      },
      { 
        path: '/admin', 
        icon: BarChart2, 
        label: 'Admin Dashboard',
        colors: {
          bg: 'bg-gradient-to-br from-fuchsia-100 to-pink-100 dark:from-fuchsia-900/20 dark:to-pink-900/20',
          icon: 'text-fuchsia-600 dark:text-fuchsia-400'
        }
      }
    );
  }

  return (
    <aside className="w-64 bg-white dark:bg-dark-800 border-r border-gray-200 dark:border-dark-700">
      <div className="h-full px-4">
        {/* Empty space where button was */}
        <div className="h-8"></div>

        <nav className="space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div className={`w-8 h-8 rounded-lg ${
                    isActive ? item.colors.bg : 'bg-gray-50/80 dark:bg-dark-700/80'
                  } flex items-center justify-center mr-3 transition-colors duration-200`}>
                    <item.icon className={`w-4 h-4 ${item.colors.icon} ${
                      !isActive && 'opacity-70'
                    }`} />
                  </div>
                  {item.label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="mt-8 pt-8 border-t border-gray-200 dark:border-dark-700">
          <div className="rounded-lg p-4 relative overflow-hidden bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500">
            {/* Decorative circles */}
            <div className="absolute top-0 right-0 w-24 h-24 -mr-6 -mt-6 rounded-full bg-white/10"></div>
            <div className="absolute bottom-0 left-0 w-16 h-16 -ml-4 -mb-4 rounded-full bg-white/10"></div>
            
            {/* Content */}
            <div className="flex items-center relative z-10">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <span className="text-white font-semibold">
                    {user?.displayName?.[0] || user?.email?.[0] || 'U'}
                  </span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-white">
                  {user?.subscription ? `${user.subscription.charAt(0).toUpperCase()}${user.subscription.slice(1)} Plan` : 'Free Plan'}
                </p>
                <p className="text-xs text-white/80">
                  {user?.charactersUsed?.toLocaleString() || '0'} / {user?.charactersLimit?.toLocaleString() || '0'} chars
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;